import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Modal, Button } from 'flowbite-react';
import { API_BASE_URL } from "../../config";

const MASTERY_THRESHOLD = 80;

const SubjectSideNavigation = ({ selectedModuleIndex, subjectName, img }) => {
  const [modules, setModules] = useState([]);
  const [moduleStates, setModuleStates] = useState({});
  const [activeModule, setActiveModule] = useState(selectedModuleIndex ?? 0);
  const [loadingModules, setLoadingModules] = useState(true);
  const [loadingMastery, setLoadingMastery] = useState(false);

  const slugify = (s = "") =>
    String(s)
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");
  
  const front_end_url = window.location.origin;
  const examSlug = "bgcse"; // or derive from props later
  const subjectSlug = slugify(subjectName);
  const SUBJECT_BASE_URL = `${front_end_url}/${examSlug}/${subjectSlug}`;

  const [lockedModalOpen, setLockedModalOpen] = useState(false);
  const [lockedModuleLabel, setLockedModuleLabel] = useState('');

  const location = useLocation();
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const studentId = userInfo?.id;

  // Study mode (guided vs explore), persisted per examtype_subject
  const modeStorageKey = `study_mode_${examSlug}_${subjectSlug}`;
  const [studyMode, setStudyMode] = useState(() => {
    const saved = localStorage.getItem(modeStorageKey);
    return saved === 'explore' ? 'explore' : 'guided'; // default guided
  });

  useEffect(() => {
    localStorage.setItem(modeStorageKey, studyMode);
  }, [modeStorageKey, studyMode]);

  // If we're on /learn view, visually deselect the side module
  useEffect(() => {
    if (location.pathname.includes('/learn')) {
      setActiveModule(-1);
    }
  }, [location.pathname]);

  // Fetch modules
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingModules(true);

        const subjectsResponse = await axios.get(`${API_BASE_URL}/subjects`);
        const filteredSubject = subjectsResponse.data.find((subject) =>
          String(subject.subject_name).trim().toLowerCase() === String(subjectName).trim().toLowerCase() &&
          String(subject.exam_type).trim().toLowerCase() === 'bgcse'
        );

        if (!filteredSubject) {
          setModules([]);
          return;
        }

        const modulesResponse = await axios.get(
          `${API_BASE_URL}/subject_modules`,
          { params: { subjectId: filteredSubject.id } }
        );

        const fetchedModules = modulesResponse.data || [];
        const sorted = [...fetchedModules].sort(
          (a, b) => a.module_number - b.module_number
        );

        setModules(sorted);
      } catch (error) {
        console.error('Error fetching modules:', error);
        setModules([]);
      } finally {
        setLoadingModules(false);
      }
    };

    fetchData();
  }, [selectedModuleIndex, subjectName]);

  // Fetch mastery + unlock states
  useEffect(() => {
    if (!studentId) return;
    if (!modules.length) return;

    const fetchMastery = async () => {
      try {
        setLoadingMastery(true);

        const statsResponses = await Promise.all(
          modules.map((mod) =>
            axios.get(`${API_BASE_URL}/api/module_mastery_statistics`, {
              params: {
                student_id: studentId,
                subject_module_id: mod.id,
              },
            })
          )
        );

        const newState = {};

        modules.forEach((module, index) => {
          const data = statsResponses[index].data || {};
          const mastery = data.mastery_percentage ?? 0;
          const unlocked = data.unlocked ?? (module.module_number === 1);

          newState[module.id] = {
            mastery,
            unlocked,
          };
        });

        setModuleStates(newState);
      } catch (err) {
        console.error('Error loading module mastery stats:', err);

        // Fallback: first module unlocked, rest locked
        const fallbackState = {};
        modules.forEach((module, index) => {
          fallbackState[module.id] = {
            mastery: 0,
            unlocked: index === 0,
          };
        });
        setModuleStates(fallbackState);
      } finally {
        setLoadingMastery(false);
      }
    };

    fetchMastery();
  }, [modules, studentId]);

  const navigateToModule = (module_number, title) => {
    setActiveModule(module_number);

    if (module_number === 0) {
      window.location.replace(SUBJECT_BASE_URL); // /bgcse/biology
      return;
    }

    const module_url = slugify(title); // safer than replace(/\s+/g, '-')
    window.location.replace(`${SUBJECT_BASE_URL}/${module_url}`);
  };

  const handleModuleClick = (module_number, title, unlocked) => {
    const lockedInGuided = !unlocked;

    // Guided mode: enforce locks
    if (studyMode === 'guided' && lockedInGuided) {
      setLockedModuleLabel(`Module ${module_number}: ${title}`);
      setLockedModalOpen(true);
      return;
    }

    // Explore mode: no warnings, just navigate
    navigateToModule(module_number, title);
  };

  const isLoading = loadingModules || loadingMastery;

  return (
    <div>
      {/* Header */}
      <div
        className="w-full h-full hover:cursor-pointer hover:bg-[#f0f0f0] px-5 pt-5 mb-2 pb-4"
        onClick={() => navigateToModule(0, subjectName)}
      >
        <div className="flex">
          <div className="mr-4 p-[2px] rounded-full bg-gradient-to-br from-sky-400 via-emerald-400 to-indigo-500">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-white">
              <img
                src={img}
                alt={`${subjectName} cover`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div>
            <h1 className="text-gray-500 text-lg">BGCSE {subjectName}</h1>
            <p className="text-gray-500 text-xs">{modules.length} MODULES</p>
          </div>
        </div>
      </div>

      {/* Study Mode Toggle */}
      <div className="px-5">
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] text-gray-500">Select Mode</p>
              <p className="text-xs font-semibold text-gray-800">
                {studyMode === 'guided' ? 'Guided' : 'Explore'}
              </p>
            </div>

            {/* Segmented control */}
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setStudyMode('guided')}
                className={`px-3 py-1 text-xs rounded-md transition ${
                  studyMode === 'guided'
                    ? 'bg-white shadow text-gray-900'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Guided
              </button>
              <button
                type="button"
                onClick={() => setStudyMode('explore')}
                className={`px-3 py-1 text-xs rounded-md transition ${
                  studyMode === 'explore'
                    ? 'bg-white shadow text-gray-900'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Explore
              </button>
            </div>
          </div>

          <p className="mt-2 text-[11px] text-gray-500 leading-snug">
            {studyMode === 'guided'
              ? `Unlock modules by reaching ${MASTERY_THRESHOLD}% mastery in the previous module.`
              : 'Jump to any module anytime. Guided locks are turned off.'}
          </p>
        </div>

        {isLoading && (
          <div className="mt-2 text-xs text-gray-400">
            Calculating your module mastery…
          </div>
        )}
      </div>

      {/* Modules */}
      {!isLoading &&
        modules.map((subject_module) => {
          const state = moduleStates[subject_module.id] || {};
          const unlocked = state.unlocked ?? false;

          const lockedInGuided = !unlocked;
          const isActive = activeModule === subject_module.module_number;

          // In guided mode we gray out locked modules; in explore mode we don't.
          const isLockedForUI = studyMode === 'guided' && lockedInGuided;

          return (
            <div
              key={subject_module.module_number}
              className={`w-full py-2 transition delay-80 ease-in-out px-5 mt-2 pt-5 ${
                isActive ? 'bg-[#f0f0f0]' : 'hover:bg-[#f0f0f0]'
              } ${isLockedForUI ? 'opacity-60' : 'cursor-pointer'}`}
              style={{ height: isActive ? 'auto' : '80px' }}
              onClick={() =>
                handleModuleClick(
                  subject_module.module_number,
                  subject_module.module_name,
                  unlocked
                )
              }
            >
              <div className="flex items-center justify-between text-left">
                <p className="text-gray-500">{`MODULE ${subject_module.module_number}`}</p>

                {/* Locked pill shown in Guided mode */}
                {studyMode === 'guided' && lockedInGuided && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                    Locked
                  </span>
                )}
              </div>
 
              <p className="font-thin text-black-400 pb-2 text-left w-full">
                {subject_module.module_name}
              </p>
              <hr className="mt-auto" />
            </div>
          );
        })}

      {/* Practice link */}
      {!isLoading && modules.length > 0 && (
        <a href={`${SUBJECT_BASE_URL}/quizzes`}>
          <div className="w-full pb-4 hover:cursor-pointer hover:bg-[#f0f0f0] transition delay-80 ease-in-out px-5 pt-5">
            <p className="text-gray-500">PRACTICE</p>
            <p className="text-black font-thin">
              {subjectName.toUpperCase()} QUIZZES
            </p>
          </div>
        </a>
      )}

      {/* Guided locked-module modal */}
      <Modal
        show={lockedModalOpen}
        size="md"
        popup
        onClose={() => setLockedModalOpen(false)}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center px-2 pb-4">
            <h3 className="mb-2 text-lg font-semibold text-gray-800">
              You’re Nearly There!
            </h3>
            <p className="text-sm text-gray-600 text-xs">
              {lockedModuleLabel
                ? `${lockedModuleLabel} will unlock once you reach at least ${MASTERY_THRESHOLD}% mastery in the previous module.`
                : `This module will unlock once you reach at least ${MASTERY_THRESHOLD}% mastery in the previous module.`}
            </p>

            <div className="mt-4 flex justify-center gap-2">
              <Button color="info" onClick={() => setLockedModalOpen(false)}>
                Got it
              </Button>
              <Button
                color="light"
                onClick={() => {
                  setLockedModalOpen(false);
                  setStudyMode('explore');
                }}
              >
                Switch to Explore
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SubjectSideNavigation;
